<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api/client';
  import QRCode from 'qrcode';
  import type { GroupSharing, Invite, LocationChoice, Member, PauseResult, PersonView, SharingItem } from '../lib/api/types';
  import { when } from '../lib/utils/time';
  import { groups } from '../lib/stores/groups';
  import { websocket } from '../lib/stores/websocket';
  import { acting, currentUser, isAuthenticated, managedBy } from '../lib/stores/auth';
  import { confirm } from '../lib/stores/confirmDialog';
  import PageHeader from '../lib/components/PageHeader.svelte';
  import Modal from '../lib/components/Modal.svelte';
  import { groupLabel } from '../lib/utils/groupLabel';
  import GroupAlerts from '../lib/components/alerts/GroupAlerts.svelte';
  import ArriveBy from '../lib/components/alerts/ArriveBy.svelte';
  import { locale, t } from '../lib/i18n/index.ts';

  const selected = groups.selected;
  const nameList = new Intl.ListFormat(locale(), { type: 'unit', style: 'short' });
  // names joins names as the region lists them, "Ana, Ben, Cy" in English.
  function names(list: string[]): string {
    return nameList.format(list);
  }
  // invitedAt writes an invite's moment as the region writes a date and
  // time, with a plain space before AM or PM.
  function invitedAt(at: string, withTime: boolean): string {
    const d = new Date(at);
    return (withTime ? d.toLocaleString(locale()) : d.toLocaleDateString(locale())).replace(/ /g, ' ');
  }

  let members: Member[] = $state([]);
  let invites: Invite[] = $state([]);
  let error = $state('');
  let newName = $state('');
  let showCreate = $state(false);
  let newInvite: Invite | null = $state(null);
  let inviteQR = $state('');
  let copied = $state(false);
  let sharing: GroupSharing | null = $state(null);
  let renaming = $state(false);
  let rename = $state('');

  // items are what a member shares per group, in the apps' order (spec
  // 001 FR-047).
  const items: { key: SharingItem; name: string; caption: string }[] = [
    { key: 'activities', name: t('groupsItemActivities'), caption: t('groupsItemActivitiesCaption') },
    { key: 'heart_rate', name: t('groupsItemHeartRate'), caption: t('groupsItemHeartRateCaption') },
    { key: 'health', name: t('groupsItemHealth'), caption: t('groupsItemHealthCaption') },
    { key: 'rest_sleep', name: t('groupsItemRestSleep'), caption: t('groupsItemRestSleepCaption') },
    { key: 'driving', name: t('groupsItemDriving'), caption: t('groupsItemDrivingCaption') },
    { key: 'phone', name: t('groupsItemPhone'), caption: t('groupsItemPhoneCaption') },
    { key: 'email', name: t('groupsItemEmail'), caption: t('groupsItemEmailCaption') },
  ];
  // lookbacks are the look-back presets (spec 012 FR-043); Custom takes
  // any number of days (spec 001 FR-064), 0 being forever.
  const lookbacks: { days: number; label: string }[] = [
    { days: 1, label: t('groupsLookbackDay') },
    { days: 7, label: t('groupsLookbackWeek') },
    { days: 30, label: t('groupsLookbackMonth') },
    { days: 90, label: t('groupsLookbackQuarter') },
    { days: 365, label: t('groupsLookbackYear') },
    { days: 0, label: t('groupsLookbackForever') },
  ];
  let lookbackError = $state('');
  // customLookback shows the days field: Custom was picked, or the saved
  // reach is not a preset.
  let customLookback = $state(false);
  // saved briefly says a sharing change took (spec 001 FR-065).
  let saved = $state(false);
  let savedTimer: ReturnType<typeof setTimeout> | undefined;
  // reverts redraws the sharing controls after a failed save: reading back
  // the same values changes nothing Svelte would write, so a control would
  // keep the choice that failed (spec 001 FR-065).
  let reverts = $state(0);
  // stillSeeing names who still sees the member's location through another
  // group while the selected group is paused (spec 001 FR-082).
  let stillSeeing: string[] = $state([]);

  let group = $derived($groups.find((c) => c.id === $selected));
  // A parent managing a child changes the child's own membership and sharing,
  // not the groups the child admins; a child changes neither (spec 001).
  let isAdmin = $derived(group?.role === 'admin' && !$acting);
  let locked = $derived(!!$managedBy);

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  // New group shows only when the server's group creation setting lets this
  // member create one (spec 001 FR-044).
  let canCreate = $state(false);
  onMount(() => {
    groups.load().catch((e: any) => (error = e.message));
    api.get<{ can_create_groups?: boolean }>('/auth/me').then((me) => (canCreate = me.can_create_groups !== false)).catch(() => {});
  });

  // Pausing (spec 001 FR-082): a group on its section, all of them at the
  // top of the page. durations are minutes; 0 lasts until the member resumes.
  const durations = [
    { label: t('groupsPauseHours', { count: 1 }), minutes: 60 },
    { label: t('groupsPauseHours', { count: 2 }), minutes: 120 },
    { label: t('groupsPauseHours', { count: 8 }), minutes: 480 },
    { label: t('groupsPauseUntilResume'), minutes: 0 },
  ];
  let pauseFor = $state(60);
  let pauseAllFor = $state(60);
  let pauseNote = $state('');

  // inviteLink lets a parent managing a child add the child to a group with
  // an invite link (spec 001 FR-100), since the child cannot accept one.
  let inviteLink = $state('');
  async function joinForChild(e: SubmitEvent) {
    e.preventDefault();
    const token = inviteLink.trim().replace(/\/+$/, '').split('/').pop() ?? '';
    try {
      await api.post(`/invites/${encodeURIComponent(token)}/accept`);
      inviteLink = '';
      error = '';
      await groups.load();
    } catch (err: any) {
      error = err.message;
    }
  }

  async function pause(ids: string[], minutes: number) {
    error = '';
    pauseNote = '';
    const body: { group_ids: string[]; until?: string } = { group_ids: ids };
    if (minutes > 0) body.until = new Date(Date.now() + minutes * 60_000).toISOString();
    try {
      const res = await api.post<PauseResult>('/me/pauses', body);
      const notes: string[] = [];
      if (res.refused.length) {
        const refused = $groups.filter((c) => res.refused.includes(c.id)).map((c) => c.name);
        notes.push(t('groupsPauseRefused', { groups: names(refused) }));
      }
      // A pause hides the member only from people with no other group that
      // sees them. A paused group's own card names them.
      if (ids.length === 0 && res.still_seeing?.length) {
        const people = res.still_seeing.map((p) => p.display_name);
        notes.push(t('groupsStillSeesLocation', { count: people.length, people: names(people) }));
      }
      pauseNote = notes.join(' ');
      await groups.load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function resume(ids: string[]) {
    error = '';
    pauseNote = '';
    const q = ids.map((id) => `group_id=${id}`).join('&');
    try {
      await api.delete(`/me/pauses${q ? `?${q}` : ''}`);
      await groups.load();
    } catch (e: any) {
      error = e.message;
    }
  }

  $effect(() => {
    const id = $selected;
    members = [];
    invites = [];
    if (!id) return;
    load(id);
    // Someone joining, leaving, or changing role shows without a reload, and
    // the invite they used leaves the open ones (001 FR-112).
    const stopTopic = websocket.subscribeTopic(`group:${id}`, (_payload, topic, type) => {
      if (topic === `group:${id}` && type === 'members') loadMembers(id);
    });
    // Changes sent while the socket was down are lost (001 FR-113).
    const stopReconnect = websocket.onReconnect(() => loadMembers(id));
    return () => {
      stopTopic();
      stopReconnect();
    };
  });

  // loadMembers reads the group's members and open invites again, leaving
  // the sharing choices the member may be changing as they are.
  async function loadMembers(id: string) {
    try {
      const m = await api.get<Member[]>(`/groups/${id}/members`);
      const inv = group?.role === 'admin' && !api.acting ? await api.get<Invite[]>(`/groups/${id}/invites`) : [];
      if (id !== $selected) return;
      members = m;
      invites = inv;
    } catch (e: any) {
      if (id === $selected) error = e.message;
    }
  }

  async function load(id: string) {
    error = '';
    try {
      const s = await api.get<GroupSharing>(`/groups/${id}/sharing`);
      const m = await api.get<Member[]>(`/groups/${id}/members`);
      const inv = group?.role === 'admin' && !api.acting ? await api.get<Invite[]>(`/groups/${id}/invites`) : [];
      // A reply for a group no longer selected is dropped.
      if (id !== $selected) return;
      sharing = s;
      customLookback = !lookbacks.some((l) => l.days === s.lookback_days);
      members = m;
      invites = inv;
    } catch (e: any) {
      if (id === $selected) error = e.message;
    }
  }

  // The people who still see the member through another group are those whose
  // shared groups, leaving out paused ones, still give a continuous level.
  $effect(() => {
    const g = group;
    const others = members.filter((m) => m.user_id !== $currentUser?.id);
    stillSeeing = [];
    if (!g?.paused_since || others.length === 0) return;
    Promise.all(others.map((m) => api.get<PersonView>(`/me/people/${m.user_id}`).catch(() => null))).then((views) => {
      if (g.id !== $selected || !group?.paused_since) return;
      stillSeeing = views
        .filter((v): v is PersonView => !!v && (v.location.level === 'detailed' || v.location.level === 'standard'))
        .map((v) => v.display_name);
    });
  });

  async function createGroup() {
    try {
      await groups.create(newName);
      newName = '';
      showCreate = false;
    } catch (e: any) {
      error = e.message;
    }
  }

  // saveSharing sends one sharing change and says it saved, or says why not
  // and shows what is saved again (spec 001 FR-065).
  async function saveSharing(change: Record<string, unknown>): Promise<boolean> {
    clearTimeout(savedTimer);
    saved = false;
    try {
      sharing = await api.patch<GroupSharing>(`/groups/${$selected}/sharing`, change);
      saved = true;
      savedTimer = setTimeout(() => (saved = false), 2000);
      return true;
    } catch (e: any) {
      // load clears the page's error, so the reason is set after it.
      await load($selected!);
      error = e.message;
      reverts++;
      return false;
    }
  }

  async function setLookback(days: number) {
    lookbackError = '';
    if (!Number.isInteger(days) || days < 0) {
      lookbackError = t('groupsLookbackInvalid');
      return;
    }
    await saveSharing({ lookback_days: days });
  }

  // locationCaptions say what each level shows the group and what it costs
  // (spec 001 FR-059). Your phone tracks as closely as the most detailed
  // of your groups asks.
  const locationCaptions: Record<LocationChoice, string> = {
    detailed: t('groupsLocationDetailedCaption'),
    standard: t('groupsLocationStandardCaption'),
    choose: t('groupsLocationChooseCaption'),
    off: t('groupsLocationOffCaption'),
  };

  // locationOptions are the choices in the Location menu: Detailed for a
  // time or until turned off (spec 001 FR-049), then the other levels. A
  // managed member keeps the group at standard or more (001 FR-085).
  let locationOptions = $derived.by(() => {
    const s = sharing as GroupSharing | null;
    const out: { value: string; label: string }[] = [];
    if (s?.location === 'detailed' && s.detailed_until) {
      out.push({ value: 'detailed-timed', label: t('groupsDetailedUntil', { when: when(s.detailed_until) }) });
    }
    out.push(
      { value: 'detailed-hour', label: t('groupsDetailedHour') },
      { value: 'detailed-day', label: t('groupsDetailedDay') },
      { value: 'detailed', label: t('groupsDetailedOn') },
      { value: 'standard', label: t('groupsStandard') },
    );
    if (!s?.managed) out.push({ value: 'choose', label: t('groupsWhenIChoose') }, { value: 'off', label: t('groupsLocationOff') });
    return out;
  });

  let locationValue = $derived.by(() => {
    const s = sharing as GroupSharing | null;
    if (s?.location !== 'detailed') return s?.location ?? 'standard';
    return s.detailed_until ? 'detailed-timed' : 'detailed';
  });

  async function setLocation(value: string) {
    if (value === 'detailed-timed') return;
    const hours = value === 'detailed-hour' ? 1 : value === 'detailed-day' ? 24 : 0;
    const choice = (value.startsWith('detailed') ? 'detailed' : value) as LocationChoice;
    const body: { location: LocationChoice; detailed_until?: string } = { location: choice };
    if (hours > 0) body.detailed_until = new Date(Date.now() + hours * 3_600_000).toISOString();
    if (await saveSharing(body)) await groups.load();
  }

  async function share(item: SharingItem, on: boolean) {
    await saveSharing({ [item]: on });
  }

  async function saveName() {
    try {
      await api.patch(`/groups/${$selected}`, { name: rename });
      renaming = false;
      await groups.load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function invite() {
    try {
      newInvite = await api.post<Invite>(`/groups/${$selected}/invites`);
      // The QR code holds the same web invite link: Kehai's scanner reads it,
      // and a phone's own camera opens the invite page (spec 001 FR-003).
      inviteQR = newInvite.url ? await QRCode.toDataURL(newInvite.url, { margin: 1, width: 220 }) : '';
      copied = false;
      await load($selected!);
    } catch (e: any) {
      error = e.message;
    }
  }

  async function copyInvite() {
    if (!newInvite?.url) return;
    await navigator.clipboard.writeText(newInvite.url);
    copied = true;
  }

  async function revoke(inv: Invite) {
    if (!(await confirm(t('groupsRevokeConfirm')))) return;
    await api.delete(`/groups/${$selected}/invites/${inv.id}`).catch((e: any) => (error = e.message));
    await load($selected!);
  }

  async function update(m: Member, patch: { role?: string; managed?: boolean }) {
    try {
      await api.patch(`/groups/${$selected}/members/${m.user_id}`, patch);
      await load($selected!);
    } catch (e: any) {
      error = e.message;
    }
  }

  async function remove(m: Member) {
    const self = m.user_id === $currentUser?.id;
    const msg = self
      ? t('groupsLeaveConfirm', { group: group?.name })
      : t('groupsRemoveConfirm', { member: m.display_name, group: group?.name });
    if (!(await confirm(msg))) return;
    try {
      await api.delete(`/groups/${$selected}/members/${m.user_id}`);
      if (self) await groups.load();
      else await load($selected!);
    } catch (e: any) {
      error = e.message;
    }
  }

  async function deleteGroup() {
    if (!(await confirm(t('groupsDeleteConfirm', { group: group?.name })))) return;
    try {
      await api.delete(`/groups/${$selected}`);
      await groups.load();
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

<div class="p-6 max-w-4xl w-full mx-auto">
  <PageHeader title={t('groupsTitle')}>
    {#if canCreate && !locked && !$acting}
      <button class="btn btn-primary" onclick={() => (showCreate = true)}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('groupsNew')}</button>
    {/if}
  </PageHeader>

  {#if error}
    <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
  {/if}

  {#if $acting}
    <form class="card p-4 mb-4 flex items-end gap-2 flex-wrap text-sm" onsubmit={joinForChild}>
      <label class="flex-1 min-w-64">
        <span class="block font-medium mb-1">{t('groupsAddChild', { member: $currentUser?.display_name })}</span>
        <input class="input w-full" bind:value={inviteLink} required placeholder={t('groupsInviteLinkHint')} aria-label={t('groupsInviteLink')} />
      </label>
      <button type="submit" class="btn btn-secondary btn-sm">{t('groupsJoin')}</button>
    </form>
  {/if}

  {#if locked}
    <p class="text-sm mb-4"><i aria-hidden="true" class="las la-lock mr-1"></i>{t('groupsManagedBy', { parent: $managedBy })}</p>
  {/if}

  {#if $groups.length === 0}
    <p class="text-text-muted">{t('groupsNone')}</p>
  {:else}
    {#if $groups.filter((c) => !c.managed).length > 1}
      <!-- Pausing every group is apart from any one group's settings (spec 001 FR-083). -->
      <fieldset disabled={locked} class="card p-4 mb-4 flex items-center gap-2 flex-wrap text-sm min-w-0">
        <span class="flex-1 min-w-48">
          <span class="block font-medium">{t('groupsAll')}</span>
          <span class="block text-xs text-text-muted">{t('groupsAllCaption')}</span>
        </span>
        {#if $groups.some((c) => c.paused_since)}
          <button class="btn btn-secondary btn-sm" onclick={() => resume([])}><i aria-hidden="true" class="las la-play mr-1"></i>{t('groupsResumeAll')}</button>
        {/if}
        <select class="input text-sm py-1 w-40" bind:value={pauseAllFor} aria-label={t('groupsPauseAllFor')}>
          {#each durations as d (d.minutes)}<option value={d.minutes}>{d.label}</option>{/each}
        </select>
        <button class="btn btn-secondary btn-sm" onclick={() => pause([], pauseAllFor)}><i aria-hidden="true" class="las la-pause mr-1"></i>{t('groupsPauseAll')}</button>
      </fieldset>
    {/if}
    {#if pauseNote}
      <p class="text-sm mb-4" style="color: var(--theme-warning);">{pauseNote}</p>
    {/if}

    <div class="flex gap-2 flex-wrap mb-4">
      {#each $groups as c (c.id)}
        <button class="btn {c.id === $selected ? 'btn-primary' : 'btn-secondary'}" onclick={() => selected.set(c.id)}>
          {groupLabel(c, $groups)}{#if c.paused_since}<span class="text-xs ml-1 opacity-80">{t('groupsPausedTag')}</span>{/if}
        </button>
      {/each}
    </div>

    {#if group}
      {#if isAdmin}
        <div class="mb-4 flex items-center gap-2">
          {#if renaming}
            <form class="flex gap-2" onsubmit={(e: SubmitEvent) => { e.preventDefault(); saveName(); }}>
              <input class="input" bind:value={rename} required maxlength="64" aria-label={t('groupsName')} />
              <button type="submit" class="btn btn-primary btn-sm">{t('appSave')}</button>
              <button type="button" class="btn btn-secondary btn-sm" onclick={() => (renaming = false)}>{t('appCancel')}</button>
            </form>
          {:else}
            <button class="btn btn-secondary btn-sm" onclick={() => { rename = group!.name; renaming = true; }}><i aria-hidden="true" class="las la-pen mr-1"></i>{t('groupsRename', { group: group.name })}</button>
          {/if}
        </div>
      {/if}

      {#if sharing}
        <fieldset disabled={locked} class="card p-5 mb-6 min-w-0">
          <PageHeader title={t('groupsWhatYouShare', { group: group.name })} level="section">
            <span class="text-sm text-text-muted" role="status">{saved ? t('groupsSaved') : ''}</span>
          </PageHeader>
          <p class="text-sm text-text-muted mb-3">{t('groupsSharedAcross')}</p>
          {#key reverts}
          <ul class="divide-y" style="border-color: var(--theme-surface1);">
            <li class="py-3">
              <label for="location" class="block font-medium">{t('groupsLocation')}</label>
              <span class="block text-sm text-text-muted mb-2">{locationCaptions[sharing.location]}</span>
              {#if sharing.managed}
                <span class="block text-xs text-text-muted mb-2">{t('groupsLocationManaged')}</span>
              {/if}
              <select id="location" class="input" value={locationValue}
                      onchange={(e) => setLocation((e.target as HTMLSelectElement).value)}>
                {#each locationOptions as c (c.value)}
                  <option value={c.value}>{c.label}</option>
                {/each}
              </select>
              {#if sharing.location !== 'detailed' && (sharing.still_seen.location ?? []).length > 0}
                {@const still = sharing.still_seen.location ?? []}
                {@const people = { count: still.length, people: names(still.map((p) => p.display_name)) }}
                <span class="block text-xs mt-1" style="color: var(--theme-warning);">{sharing.location === 'standard' ? t('groupsStillSeesDetail', people) : t('groupsStillSeesLive', people)}</span>
              {/if}
            </li>
            {#each items as it (it.key)}
              {@const still = sharing.still_seen[it.key] ?? []}
              <li class="py-3">
                <label class="flex items-start gap-3">
                  <input type="checkbox" class="mt-1" checked={sharing[it.key] as boolean}
                         onchange={(e) => share(it.key, (e.target as HTMLInputElement).checked)} />
                  <span class="flex-1">
                    <span class="block font-medium">{it.name}</span>
                    <span class="block text-sm text-text-muted">{it.caption}</span>
                    {#if !sharing[it.key] && still.length > 0}
                      <span class="block text-xs mt-1" style="color: var(--theme-warning);">{t('groupsStillSeesIt', { count: still.length, people: names(still.map((p) => p.display_name)) })}</span>
                    {/if}
                  </span>
                </label>
              </li>
            {/each}
            <li class="py-3">
              <label for="lookback" class="block font-medium">{t('groupsHistory')}</label>
              <span class="block text-sm text-text-muted mb-2">{t('groupsHistoryCaption', { group: group.name })}</span>
              <div class="flex flex-wrap items-center gap-2">
                <select id="lookback" class="input"
                        value={customLookback ? 'custom' : String(sharing.lookback_days)}
                        onchange={(e) => {
                          const v = (e.target as HTMLSelectElement).value;
                          customLookback = v === 'custom';
                          if (!customLookback) setLookback(Number(v));
                        }}>
                  {#each lookbacks as l (l.days)}
                    <option value={String(l.days)}>{l.label}</option>
                  {/each}
                  <option value="custom">{t('groupsLookbackCustom')}</option>
                </select>
                {#if customLookback}
                  <input type="number" min="1" max="36500" class="input w-24" aria-label={t('groupsDaysBack')} value={sharing.lookback_days || ''}
                         onchange={(e) => setLookback(Number((e.target as HTMLInputElement).value))} />
                  <span class="text-sm text-text-muted">{t('groupsDaysBackUnit')}</span>
                {/if}
              </div>
              {#if lookbackError}<p class="text-xs mt-1" style="color: var(--theme-error);">{lookbackError}</p>{/if}
            </li>
          </ul>
          {/key}
        </fieldset>
      {/if}

      <fieldset disabled={locked} class="card p-5 mb-6 min-w-0">
        <PageHeader title={t('groupsPause')} level="section" />
        <p class="text-sm text-text-muted mb-3">{t('groupsPauseCaption', { group: group.name })}</p>
        {#if group.managed}
          <p class="text-sm">{t('groupsPauseManaged')}</p>
        {:else if group.paused_since}
          <div class="flex items-center gap-3 flex-wrap">
            <span class="text-sm flex-1">{group.paused_until
              ? t('groupsPausedUntil', { since: when(group.paused_since), until: when(group.paused_until) })
              : t('groupsPausedUntilResume', { since: when(group.paused_since) })}</span>
            <button class="btn btn-primary btn-sm" onclick={() => resume([group!.id])}>{t('groupsResume')}</button>
          </div>
          {#if stillSeeing.length > 0}
            <p class="text-sm mt-2" style="color: var(--theme-warning);">{t('groupsStillSeesLocation', { count: stillSeeing.length, people: names(stillSeeing) })}</p>
          {/if}
        {:else}
          <div class="flex items-center gap-2 flex-wrap">
            <select class="input text-sm py-1 w-40" bind:value={pauseFor} aria-label={t('groupsPauseFor', { group: group.name })}>
              {#each durations as d (d.minutes)}<option value={d.minutes}>{d.label}</option>{/each}
            </select>
            <button class="btn btn-secondary btn-sm" onclick={() => pause([group!.id], pauseFor)}>{t('groupsPauseWith', { group: group.name })}</button>
          </div>
        {/if}
      </fieldset>

      {#if !$acting}
        <GroupAlerts groupId={group.id} groupName={group.name} {members} />
        <ArriveBy groupId={group.id} {members} />
      {/if}

      <div class="card p-5 mb-6">
        <PageHeader title={t('groupsMembers')} level="section">
          {#if isAdmin}
            <button class="btn btn-secondary btn-sm" onclick={invite}><i aria-hidden="true" class="las la-user-plus mr-1"></i>{t('groupsInvite')}</button>
          {/if}
        </PageHeader>
        <ul class="divide-y" style="border-color: var(--theme-surface1);">
          {#each members as m (m.user_id)}
            <li class="py-3 flex flex-wrap items-center gap-3">
              <span class="flex-1 min-w-48">
                <span class="font-medium">{m.display_name}</span>
                <span class="text-xs text-text-muted ml-2">@{m.username}</span>
                {#if m.role === 'admin'}<span class="text-xs ml-2 px-2 py-0.5 rounded bg-surface1">{t('groupsRoleAdmin')}</span>{/if}
                {#if m.managed}<span class="text-xs ml-2 px-2 py-0.5 rounded bg-surface1">{t('groupsManaged')}</span>{/if}
                {#if m.paused_since}<span class="text-xs ml-2 px-2 py-0.5 rounded bg-surface1">{t('groupsPaused')}</span>{/if}
                {#if m.tracking_mode === 'detailed'}<span class="text-xs ml-2 px-2 py-0.5 rounded bg-surface1">{t('groupsDetailed')}</span>{/if}
                {#if m.tracking_mode === 'manual'}<span class="text-xs ml-2 text-text-muted"><i aria-hidden="true" class="las la-hand-paper"></i>{t('stateManual')}</span>{/if}
                <span class="text-xs ml-2 text-text-muted" title={t('groupsCrashHint', { member: m.display_name })}>
                  <i aria-hidden="true" class="las la-car-crash"></i>{m.crash_detection ? t('groupsCrashOn') : t('groupsCrashOff')}
                </span>
              </span>
              {#if m.user_id !== $currentUser?.id}
                <!-- What this person gets of you across every group you share (spec 001 FR-079). -->
                <button class="btn btn-secondary btn-sm" onclick={() => navigate(`/people/${m.user_id}`)}>
                  <i aria-hidden="true" class="las la-eye mr-1"></i>{t('groupsWhatTheySee')}
                </button>
              {/if}
              {#if isAdmin && m.user_id !== $currentUser?.id}
                <label class="text-xs flex items-center gap-1" title={t('groupsManagedHint')}>
                  <input type="checkbox" checked={m.managed} disabled={m.role === 'admin'}
                         onchange={(e) => update(m, { managed: (e.target as HTMLInputElement).checked })} />
                  {t('groupsManaged')}
                </label>
                <select class="input text-xs py-1" value={m.role} disabled={m.managed}
                        onchange={(e) => update(m, { role: (e.target as HTMLSelectElement).value })}>
                  <option value="member">{t('groupsRoleMember')}</option>
                  <option value="admin">{t('groupsRoleAdmin')}</option>
                </select>
                <button class="btn btn-secondary btn-sm" onclick={() => remove(m)} aria-label={t('groupsRemoveMember', { member: m.display_name })}>
                  <i aria-hidden="true" class="las la-user-minus"></i>
                </button>
              {:else if m.user_id === $currentUser?.id && !m.managed && !locked}
                <button class="btn btn-secondary btn-sm" onclick={() => remove(m)}>{t('groupsLeave')}</button>
              {/if}
            </li>
          {/each}
        </ul>
      </div>

      {#if isAdmin}
        <div class="card p-5 mb-6">
          <PageHeader title={t('groupsOpenInvites')} level="section" />
          {#if invites.length === 0}
            <p class="text-sm text-text-muted">{t('groupsNoInvites')}</p>
          {:else}
            <ul class="text-sm">
              {#each invites as inv (inv.id)}
                <li class="py-2 flex items-center gap-3">
                  <span class="flex-1">{t('groupsInviteDates', { created: invitedAt(inv.created_at, true), expires: invitedAt(inv.expires_at, false) })}</span>
                  <button class="btn btn-secondary btn-sm" onclick={() => revoke(inv)}>{t('groupsRevoke')}</button>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <button class="btn btn-danger" onclick={deleteGroup}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('groupsDelete')}</button>
      {/if}
    {/if}
  {/if}
</div>

{#if showCreate}
  <Modal title={t('groupsNew')} onclose={() => (showCreate = false)}>
    <form class="flex flex-col gap-4" onsubmit={(e: SubmitEvent) => { e.preventDefault(); createGroup(); }}>
      <div>
        <label for="group-name" class="block text-sm font-medium mb-1">{t('groupsNewName')}</label>
        <input id="group-name" class="input w-full" bind:value={newName} required maxlength="64" placeholder={t('groupsNewNameHint')} />
      </div>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (showCreate = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary">{t('groupsCreate')}</button>
      </div>
    </form>
  </Modal>
{/if}

{#if newInvite}
  <Modal title={t('groupsInviteLink')} onclose={() => (newInvite = null)}>
    <p class="text-sm">{t('groupsInviteAbout')}</p>
    {#if inviteQR}
      <img src={inviteQR} alt={t('groupsInviteQr')} class="rounded-md bg-white p-2 mx-auto" width="220" height="220" />
    {/if}
    <input class="input w-full text-xs" readonly value={newInvite.url} onfocus={(e) => (e.target as HTMLInputElement).select()} />
    <div class="flex justify-end gap-2">
      <button class="btn btn-primary" onclick={copyInvite}>{copied ? t('appCopied') : t('groupsCopyLink')}</button>
    </div>
  </Modal>
{/if}
